import React, { useEffect } from "react";
import createUserTableHeaders from "./createUserTableHeaders";
import UserTableRow from "./UserTableRow/UserTableRow";
import { DPTable, DPComponentLoader, DPErrorMessage } from "Components/DP";
import ZeroResults from "./ZeroResults/ZeroResults";
import { usePartnerConfig } from "Hooks/usePartnerConfig";
import { useEmployeeProfilesQuery } from "Hooks/useEmployeeProfilesQueryContext";
import { useHistory } from "Hooks/useRouter";
import { goToUserDetails } from "Hooks/useGoTo";

const defaultColWidths = [
  { columnKey: "firstName", width: 13 },
  { columnKey: "lastName", width: 13 },
  { columnKey: "primaryId", width: 13 },
  { columnKey: "location", width: 13 },
  { columnKey: "payGroups", width: 12 },
  { columnKey: "dailyPayStatus", width: 12 },
  { columnKey: "employmentStatus", width: 12 },
  { columnKey: "benefitType", width: 12 },
];

const UserTable = () => {
  const {
    employeeProfilesResponse,
    loading,
    error,
    queryProps,
    goToPage,
  } = useEmployeeProfilesQuery();

  const { page, ...restQueryProps } = queryProps;
  const searchQuery = queryProps.search;

  const history = useHistory();
  useEffect(() => {
    if (
      searchQuery &&
      employeeProfilesResponse &&
      employeeProfilesResponse.totalCount === 1
    ) {
      if (!employeeProfilesResponse.items.length) {
        goToPage({ ...restQueryProps, page: 1 });
      } else {
        goToUserDetails(history)(employeeProfilesResponse.items[0].id);
      }
    }
  }, [
    searchQuery,
    employeeProfilesResponse,
    history,
    goToPage,
    restQueryProps,
  ]);

  const { companies, associatedLocationsCount } = usePartnerConfig();
  const hideColumns = companies.length > 1 ? [] : ["payGroups"];
  if (!associatedLocationsCount) {
    hideColumns.push("location");
  }

  const colWidths = defaultColWidths
    .filter(({ columnKey }) => !hideColumns.includes(columnKey))
    .map(({ width }) => width);

  const onChangePageFn = (
    evt: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => goToPage({ ...restQueryProps, page: newPage });

  if (error) return <DPErrorMessage />;

  const headers = createUserTableHeaders({
    queryProps,
    hideColumns,
    updateSort: (newSort) => goToPage({ ...queryProps, sort: newSort }),
  });

  return (
    <DPTable
      colWidths={colWidths}
      count={employeeProfilesResponse.totalCount}
      headers={headers}
      id="user-info"
      loading={loading}
      onChangePage={onChangePageFn}
      page={page}
      rowsPerPage={+process.env.REACT_APP_USER_ROWS_PER_PAGE!}
    >
      <DPComponentLoader
        isLoading={loading}
        kind="table"
        awaitComponent={
          employeeProfilesResponse.totalCount ? (
            <>
              {employeeProfilesResponse.items.map((employeeProfile) => (
                <UserTableRow
                  key={employeeProfile.id}
                  {...employeeProfile}
                  hideColumns={hideColumns}
                />
              ))}
            </>
          ) : (
            <ZeroResults searchQuery={searchQuery} />
          )
        }
      />
    </DPTable>
  );
};

export default UserTable;
